import { mapGetters } from 'vuex';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import droplets from '@editor/droplets/droplets.config.json';

const recycleDroplet = {
  computed: {
    ...mapGetters(['currentSite', 'currentPage']),
  },
  mixins: [guid],
  data() {
    return {
      activeDrp: null,
    };
  },
  methods: {
    async routeRecycleAction(params) {
      // this will figure out if this droplet is a puddle or not or if the
      // droplet has multiple or single links so that it can run the right
      // functions
      const droplet = droplets[params.type.name];
      this.activeDrp = params.droplet;
      this.activeDrp.createdDate = new Date().toISOString();
      // look for array's with non link _id's that need to be updated
      this.updateInnerIds(params);
      // if page is a template, need to turn on template region for that droplet
      // if droplet was saved as recycled in a template, need to turn it off if on a page
      params.droplet.templateRegion = this.currentPage.type === 'template';
      // next we need to check if this droplet has id folders tied to it,
      // if so we then need to make copies of it for this new recycled droplet
      if (droplet.storage && droplet.storage.idFolder) {
        this.runAssetChecks(params);
      }
      // if puddle recursively search for child droplets
      if ((params.type.puddle || params.type.section)) {
        await this.deepFindDroplets(params);
      }
    },
    async runAssetChecks(params) {
      const droplet = droplets[params.type.name];
      if (droplet.storage.assetList) {
        // loop list of assets and copy all to new id folder
        await Promise.all(params.droplet[droplet.storage.assetList]
          .map(async item => this.copyRecycleAsset({ ...params, item })));
      } else {
        // copy single asset
        await this.copyRecycleAsset({ ...params, item: params.droplet });
      }
    },
    copyRecycleAsset(params) {
      // this is used to copy assets for droplets that have unique _id folders
      return new Promise(async (resolve, reject) => {
        try {
          const droplet = droplets[params.type.name];
          const assetKey = droplet.storage.assetKey;
          if (params.item[assetKey] && !params.item[assetKey].match(/cdn\.drzzle\.app/gi)) {
            let thumbnail = false;
            if (droplet.storage.thumbnails) {
              thumbnail = this._getThumbFromFile(params.item[assetKey]);
            }
            const payload = await this._SEND([{
              name: 'copyRecycledDropletAsset',
              payload: {
                site: {
                  _id: this.currentSite._id,
                  name: this.currentSite.name,
                },
                thumbnail,
                data: params,
              },
            }]);
            this.$store.dispatch('setDropletVal', {
              drp: params.item,
              key: 'src',
              val: `/${payload.newPath}/${payload.file}`,
            });
            resolve();
          } else {
            resolve();
          }
        } catch (e) {
          reject(e.message || e);
        }
      });
    },
    async deepFindDroplets(params) {
      // this function will fish for droplets in puddles and run diagnoses on them
      const drpConfig = droplets[params.type.name];
      const drp = params.droplet;
      const subKey = drpConfig.recycle ? drpConfig.recycle.newIds : false;
      const subList = params.type.puddle && subKey ? drp[subKey.split('.')[0]] : drp.droplets;
      await this._deepFind(
        {
          entry: subList,
          key: '_id',
        }, async (item) => {
          const isDroplet = this._dropletType(item);
          if (isDroplet.any) {
            const newId = this.guid();
            this.updateStyleString({
              oldId: item._id,
              newId,
              droplet: item,
            });
            await this.routeRecycleAction({
              action: params.action,
              newId,
              type: isDroplet,
              droplet: item,
              nested: true,
            });
            item._id = newId;
          }
        },
      );
    },
    updateInnerIds(params) {
      // this helper will update _id's of inner items that need to be changed
      // when creating copies to recycle. See "recycle" keys in droplets.config.json
      // for examples on formatting for normal/nested types
      const droplet = droplets[params.type.name];
      if (droplet.recycle && droplet.recycle.newIds) {
        let levelIndex = 0;
        const loopItems = (p) => {
          const key = p.levels[levelIndex];
          const innerObj = key.split(' ');
          const isObj = innerObj.length === 2;
          const list = isObj ? p.drp[innerObj[0]][innerObj[1]] : p.drp[key];
          _.forEach(list, async (item) => {
            const newId = this.guid();
            const oldId = _.clone(item._id);
            this.updateStyleString({
              oldId,
              newId,
              droplet: item,
            });
            item._id = newId;
            if (droplet.recycle.hasCssIds && oldId) {
              await this.updateNewCssId({ oldId, newId });
            }
            const next = levelIndex + 1;
            if (p.levels[next]) {
              levelIndex += 1;
              loopItems({ drp: item, levels: p.levels });
            }
          });
        };

        const levels = droplet.recycle.newIds.split('.');
        // take the first one then loop it
        loopItems({ drp: params.droplet, levels });
      }
    },
    async updateNewCssId(params) {
      await this._deepFind(
        {
          entry: this.activeDrp.mediaQueries,
          key: 'selector',
        }, async (item) => {
          const oldSelector = new RegExp(`#drp-${params.oldId}`, 'gi');
          const newSelector = `#drp-${params.newId}`;
          if (item.selector && item.selector.match(oldSelector)) {
            item.selector = newSelector;
            this.activeDrp.pageCSS = this.activeDrp.pageCSS.replace(
              oldSelector, newSelector);
          }
        },
      );
    },
    updateStyleString(params) {
      const oldSelector = new RegExp(`#drp-${params.oldId}`, 'gi');
      const newSelector = `#drp-${params.newId}`;
      if (params.droplet.pageCSS) {
        // updateStyleString
        params.droplet.pageCSS = params.droplet.pageCSS.replace(
          oldSelector, newSelector,
        );
      }
    },
  },
};

export { recycleDroplet };



// WEBPACK FOOTER //
// ./src/mixins/recycle-droplet.js